import { useState } from "react";
import { Link, useLocation } from "react-router";
import { RouteFactory } from "@app/routePaths";
import btShortLogo from "@assets/icons/icon-bt.svg";
import logoBT50 from "@assets/logo-50.png";
import { useHeaderState } from "@components/Header/helpers";
import { useSetNavigationWidth } from "@components/Header/hooks/useSetNavigationWidth";
import SubscribeTrigger from "@components/Header/SubscribeTrigger/SubscribeTrigger";
import IconImg from "@components/Icon/IconImg";
import { IconTypeEnum } from "@components/Icon/utils/types";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";
import NotificationToasts from "@features/notifications/NotificationToasts/NotificationToasts";
import PosthogFeature from "@features/posthog/PosthogFeature";
import ArticleToolbar from "@features/toolbar/ArticleToolbar/ArticleToolbar";
import useIsAppWebView from "@hooks/useIsAppWebView";
import { useWindowSize } from "@hooks/useWindowSize";
import MyAccountHeader from "@routes/MyAccount/components/MyAccountHeader/MyAccountHeader";
import MyAccountHeaderAnonTrigger from "@routes/MyAccount/components/MyAccountHeader/MyAccountHeaderAnonTrigger.tsx";
import { sectionNavigationItems } from "@util/constant";
import { cn } from "@util/helpers";

import LogoMasthead from "./LogoMasthead/LogoMasthead";
import { Navigation } from "./Navigation/Navigation";
import SearchTrigger from "./SearchTrigger/SearchTrigger";
import SideMenuModal from "./SideMenu/SideMenuModal";
import ToggleSideMenuButton from "./ToggleSideMenuButton/ToggleSideMenuButton";
import { getMastheadLogo } from "./utils/helpers";
import type { HeaderCommonProps, HeaderProps } from "./types";
import HeaderBreadcrumb from "../HeaderBreadcrumb/HeaderBreadcrumb";

import styles from "./Header.module.scss";

export type SocialLinkConfig = {
  /**
   * @description icon image of the social link
   */
  src: string;
  link?: string;
};

export default function Header({
  displayBreadcrumb = false,
  parentCategory = "",
  childCategory,
  article,
  displayArticle,
  isFromHome,
  enableTopStoriesLink = true,
  isPremium = false,
  handleGenerateGiftURLModal,
  setIsModalOpen,
  isShownMasthead,
  isDisplayEditionSwitcher,
  headerData = [],
  className,
  textClassName,
  borderClassName,
  editionRef,
  iconsBgClassName,
  subscribeClassName,
  userIconStrokeColor,
}: HeaderCommonProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      <HeaderContainer
        headerData={headerData}
        displayBreadcrumb={displayBreadcrumb}
        setIsOpen={setIsOpen}
        parentCategory={parentCategory}
        childCategory={childCategory}
        displayArticle={displayArticle}
        article={article}
        isFromHome={isFromHome}
        enableTopStoriesLink={enableTopStoriesLink}
        isPremium={isPremium}
        handleGenerateGiftURLModal={handleGenerateGiftURLModal}
        setIsModalOpen={setIsModalOpen}
        isShownMasthead={isShownMasthead}
        className={className}
        textClassName={textClassName}
        borderClassName={borderClassName}
        iconsBgClassName={iconsBgClassName}
        subscribeClassName={subscribeClassName}
        userIconStrokeColor={userIconStrokeColor}
      />

      <SideMenuModal
        headerData={headerData}
        isOpen={isOpen}
        setIsClose={handleClose}
        isDisplayEditionSwitcher={isDisplayEditionSwitcher}
        editionRef={editionRef}
      />
    </>
  );
}

function HeaderContainer({
  headerData,
  displayBreadcrumb = false,
  setIsOpen,
  parentCategory = "",
  childCategory = "",
  displayArticle,
  article,
  isFromHome,
  enableTopStoriesLink = true,
  isPremium,
  handleGenerateGiftURLModal,
  setIsModalOpen,
  isShownMasthead,
  className,
  textClassName,
  borderClassName,
  iconsBgClassName,
  subscribeClassName,
  userIconStrokeColor,
}: HeaderProps) {
  const { displayName, isSubscriber } = useUserInfo();
  const { headerRef, headerWidth } = useHeaderState();
  const { pathname } = useLocation();
  const isAppWebView = useIsAppWebView();
  const { isScreenLG } = useWindowSize();
  const mastheadLogoDeets = getMastheadLogo(`/${parentCategory}`);
  const isGlobal = pathname === RouteFactory.global;
  const isGlobalMobile = !isScreenLG && isGlobal;
  const isHomePage = pathname === RouteFactory.home;
  const isThrivePage = pathname.startsWith(RouteFactory.thrive);
  const toggleSideMenu = () => {
    setIsOpen(true);
  };

  const { navRightContainerRef, navLeftContainerRef } = useSetNavigationWidth({
    headerWidth,
    isFromHome,
  });

  const displayReuseContentObj = {
    isDisplayed: article?.copyright === "SPH",
    publishedDate: article?.publishTime || "",
    title: article?.title || "",
  };

  return (
    <>
      <header
        ref={headerRef}
        data-testid="navigation-bar"
        className={cn(
          styles.componentHeader,
          "no-print sticky left-0 top-[-1px] z-30 w-full flex-none"
        )}
      >
        <div
          className={cn(
            "relative flex min-h-[50px] w-full items-center border-b border-gray-175 bg-white",
            { "min-h-[60px]": article && displayArticle },
            className
          )}
        >
          <div
            className={cn(
              "mx-auto flex h-full min-h-[50px] w-full max-w-[1900px] px-2 lg:px-6",
              { "justify-between": !isFromHome },
              { "justify-normal": displayBreadcrumb },
              { "justify-between lg:justify-normal": isFromHome },
              { "min-h-[60px]": article },
              { "max-w-[1200px]": article && displayArticle }
            )}
          >
            <div
              className={cn(
                "flex items-center",
                { "overflow-hidden": isFromHome },
                { "hidden lg:flex lg:flex-none": displayArticle && article }
              )}
              ref={navLeftContainerRef}
            >
              <ToggleSideMenuButton
                onClick={toggleSideMenu}
                textClassName={textClassName}
                rootClassName={iconsBgClassName}
              />

              <div
                className={cn("hidden flex-none lg:block", {
                  "lg:hidden": article && displayArticle,
                })}
              >
                <SearchTrigger
                  className={cn(textClassName, iconsBgClassName)}
                />
              </div>

              {article && displayArticle ? (
                <Link
                  className="hidden flex-none border-0 !outline-none focus-within:outline-0 focus:outline-0 lg:block lg:hover:rounded-full lg:hover:bg-gray-250"
                  to={RouteFactory.home}
                  target="_blank"
                >
                  <img src={btShortLogo} alt="BT LOGO" width={36} height={36} />
                </Link>
              ) : null}

              {isFromHome ? (
                <div className="hidden pl-3 lg:block">
                  <Navigation
                    data={headerData}
                    position="left"
                    textClassName={textClassName}
                  />
                </div>
              ) : null}
            </div>

            <div
              className={cn(
                "absolute left-1/2 top-1/2 block w-[241px] -translate-x-1/2 -translate-y-1/2 lg:w-auto",
                { "flex flex-row items-center": !isHomePage && !isThrivePage },
                { hidden: displayArticle && article },
                { "flex lg:hidden": isFromHome || displayBreadcrumb },
                { "!flex": isShownMasthead }
              )}
            >
              <LogoMasthead {...mastheadLogoDeets} rootClassName="mx-0" />

              {!isHomePage && !isThrivePage ? (
                <img
                  src={logoBT50}
                  alt="business-time-50"
                  width={isGlobalMobile ? 51 : 33}
                  height={isGlobalMobile ? 33 : 21}
                  className="ml-1"
                />
              ) : null}
            </div>

            {article && displayArticle ? (
              <>
                <div
                  className={cn(
                    "absolute left-1/2 top-1/2 hidden w-full flex-1 -translate-x-1/2 -translate-y-1/2 items-center px-3 min-[991px]:max-w-[643px] lg:block min-[1170px]:max-w-[784px]"
                  )}
                >
                  <div className={cn("lg:w-[400px] min-[1170px]:w-[500px]")}>
                    <p className="!mb-[0.563rem] mt-[0.563rem] line-clamp-2 font-lct font-bold leading-tight text-gray-850 lg:!mb-1 lg:mt-1">
                      {article.title}
                    </p>
                  </div>
                </div>
              </>
            ) : null}

            {isFromHome ? (
              <div
                className={cn(
                  "hidden w-auto min-w-0 items-center justify-end overflow-hidden min-[991px]:w-[100px] min-[991px]:min-w-[100px] lg:ml-auto lg:flex"
                )}
                ref={navRightContainerRef}
              >
                <div className="hidden lg:block">
                  <Navigation
                    data={headerData}
                    position="right"
                    textClassName={textClassName}
                  />
                </div>
              </div>
            ) : null}

            {displayBreadcrumb ? (
              <HeaderBreadcrumb
                parentCategory={parentCategory}
                childCategory={childCategory}
                categoryDataMapping={sectionNavigationItems}
                enableTopStoriesLink={enableTopStoriesLink}
              />
            ) : null}

            {article && displayArticle ? (
              <div className="flex w-full items-center justify-between lg:ml-auto lg:w-auto lg:justify-end">
                <Link to={RouteFactory.home} target="_blank">
                  <IconImg
                    icon={IconTypeEnum.chrevonLeftDarkArrow}
                    iconSize={30}
                    className="cursor-pointer lg:hidden"
                  />
                </Link>

                {!isAppWebView ? (
                  <ArticleToolbar
                    articleTitle={article.title}
                    shareUrl={article.shortUrl || ""}
                    isPremium={isPremium}
                    handleGenerateGiftURLModal={handleGenerateGiftURLModal}
                    setIsModalOpen={setIsModalOpen}
                    keyword={article.tags}
                    cueId={article.id}
                    displayPurchase={displayReuseContentObj}
                    isFromHeader
                    isSummaryAvailable={article.isSummaryAvailable}
                  />
                ) : null}
              </div>
            ) : null}

            <div
              className={cn(
                "flex flex-none items-center",
                { "!hidden": displayArticle && article },
                { "ml-auto": displayBreadcrumb }
              )}
            >
              <div
                className={cn("flex items-center justify-center ", {
                  "lg:mr-3": !isSubscriber,
                })}
              >
                {displayName ? (
                  <MyAccountHeader
                    textClassName={textClassName}
                    userIconStrokeColor={userIconStrokeColor}
                  />
                ) : (
                  <PosthogFeature flagName="feature_enable_user_login">
                    {isFromHome ? (
                      <div
                        className={cn(
                          "mx-3 hidden h-[24px] flex-none border-l border-gray-175 lg:block",
                          borderClassName
                        )}
                      ></div>
                    ) : null}

                    <MyAccountHeaderAnonTrigger textClassName={textClassName} />
                  </PosthogFeature>
                )}
              </div>

              {!isSubscriber ? (
                <PosthogFeature flagName="feature_enable_user_login">
                  <div className={cn("hidden lg:block")}>
                    <SubscribeTrigger rootClassName={subscribeClassName} />
                  </div>
                </PosthogFeature>
              ) : null}
            </div>
          </div>
        </div>

        <NotificationToasts />
      </header>
    </>
  );
}
